import { Text, Tooltip } from '@mantine/core';
import { useEffect, useState } from 'react';

type BuildInfo = { version: string; sha: string };

// Baked into the bundle at build time by Vite (vite.config.ts). Used in
// local dev and as the fallback when the runtime version.json can't be
// loaded (missing file, offline, parse error).
const BAKED: BuildInfo = { version: __APP_VERSION__, sha: __GIT_SHA__ };

/**
 * Build-info label shown at the bottom of the navbar.
 *
 * deploy.sh writes `/version.json` into each SPA bucket on every deploy,
 * stamped with the repo HEAD sha — even when the bundle itself wasn't
 * rebuilt (changed-only deploys leave the SPA untouched). Fetching it at
 * runtime makes the label track the *deployed* commit rather than whenever
 * this bundle last happened to be built, so sandbox matches local HEAD.
 * Falls back to the Vite-baked values in dev or if the fetch fails.
 *
 * The SHA pins the running build for pairing a UI bug report with a commit.
 */
export function VersionLabel() {
  const [info, setInfo] = useState<BuildInfo>(BAKED);

  useEffect(() => {
    let cancelled = false;
    fetch('/version.json', { cache: 'no-store' })
      .then((res) => (res.ok ? res.json() : null))
      .then((data: Partial<BuildInfo> | null) => {
        // In production a missing version.json is rewritten to index.html
        // (CloudFront 403→/index.html), so res.json() rejects and we land
        // in .catch. Guard against an unexpected shape here too.
        if (cancelled || !data || typeof data.sha !== 'string') return;
        setInfo({ version: data.version ?? BAKED.version, sha: data.sha });
      })
      .catch(() => {
        /* keep the baked fallback */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Tooltip label={`Build SHA: ${info.sha}`} withArrow position="top">
      <Text size="xs" c="dimmed" ta="center" px="sm" py="xs" aria-label="Build version">
        v{info.version} · {info.sha}
      </Text>
    </Tooltip>
  );
}
